/**
 * Zumvexa 2.0 — collection and search filtering.
 *
 * Loaded only on collection and search pages. Filters, sorting and
 * "Load more" re-render through the Section Rendering API without a page
 * reload. Without JavaScript the filter form is a normal GET form and
 * "Load more" is a normal link, so nothing depends on this file.
 */

// DOMParser runs with scripting off; drop <noscript> fallbacks so they don't go live.
const parseHTML = (html) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('noscript').forEach((node) => node.remove());
  return doc;
};

const debounce = (fn, wait) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
};

// Regions swapped after each filter change.
const REGIONS = ['[data-toolbar]', '[data-active-facets]', '[data-facets-body]', '[data-facets-foot]', '[data-results-area]'];

class FacetFilters extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.section;
    this.form = this.querySelector('[data-facets-form]');
    if (!this.form) return;

    const submitSoon = debounce(() => this.submit(), 450);

    this.addEventListener('change', (event) => {
      if (event.target.form === this.form && !event.target.matches('[data-price-input]')) this.submit();
    });
    this.addEventListener('input', (event) => {
      if (event.target.matches('[data-price-input]')) submitSoon();
    });
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.submit();
    });
    this.addEventListener('click', (event) => {
      const link = event.target.closest('[data-facet-link]');
      if (link) {
        event.preventDefault();
        this.load(new URL(link.href, location.origin));
        return;
      }
      const more = event.target.closest('[data-load-more]');
      if (more) {
        event.preventDefault();
        this.loadMore(more);
      }
    });

    this.onPopState = () => this.load(new URL(location.href), false);
    addEventListener('popstate', this.onPopState);

    // A drawer left open while resizing up to desktop would stay modal.
    this.desktop = matchMedia('(min-width: 990px)');
    this.onBreakpoint = () => this.desktop.matches && this.querySelector('dialog.facets[open]')?.close();
    this.desktop.addEventListener('change', this.onBreakpoint);
  }

  disconnectedCallback() {
    removeEventListener('popstate', this.onPopState);
    this.desktop?.removeEventListener('change', this.onBreakpoint);
  }

  submit() {
    const params = new URLSearchParams(new FormData(this.form));
    for (const [key, value] of [...params]) {
      if (value === '') params.delete(key);
    }
    const url = new URL(this.form.action, location.origin);
    url.search = params.toString();
    this.load(url);
  }

  async load(url, push = true) {
    this.controller?.abort();
    this.controller = new AbortController();

    const request = new URL(url);
    request.searchParams.delete('page');
    request.searchParams.set('section_id', this.sectionId);
    this.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(request, { signal: this.controller.signal });
      if (!response.ok) throw new Error(response.statusText);
      this.render(parseHTML(await response.text()));
      url.searchParams.delete('page');
      if (push) history.pushState({ facets: true }, '', url);
    } catch (error) {
      if (error.name !== 'AbortError') location.assign(url);
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  render(doc) {
    const focusedId = document.activeElement?.id;
    const openState = new Map(
      [...this.querySelectorAll('details[data-facet]')].map((details) => [details.dataset.facet, details.open])
    );

    for (const selector of REGIONS) {
      const fresh = doc.querySelector(selector);
      const current = this.querySelector(selector);
      if (fresh && current) current.replaceWith(fresh);
    }

    this.querySelectorAll('details[data-facet]').forEach((details) => {
      if (openState.has(details.dataset.facet)) details.open = openState.get(details.dataset.facet);
    });
    if (focusedId) document.getElementById(focusedId)?.focus({ preventScroll: true });
    window.theme?.reveal?.(this);
  }

  async loadMore(button) {
    if (button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');

    const request = new URL(button.href, location.origin);
    request.searchParams.set('section_id', this.sectionId);

    try {
      const response = await fetch(request);
      if (!response.ok) throw new Error(response.statusText);
      const doc = parseHTML(await response.text());
      const grid = this.querySelector('[data-results]');
      doc.querySelectorAll('[data-results] > li').forEach((item) => grid.append(document.adoptNode(item)));

      const pagination = this.querySelector('[data-pagination]');
      const fresh = doc.querySelector('[data-pagination]');
      if (pagination && fresh) pagination.replaceWith(fresh);
      else pagination?.remove();

      window.theme?.reveal?.(grid);
    } catch {
      location.assign(button.href);
    }
  }
}

if (!customElements.get('facet-filters')) customElements.define('facet-filters', FacetFilters);
